import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { VALUE_PROPOSITIONS_LIST_PATH } from '../constants/pagePath';
import { addNotification } from '../helpers/notification';
import {
  fundamentalsPath,
  linkablePageStatus,
  fromVpListState,
  openedFromFundamentals,
  openedFromVpList,
  resolveLegacyPath,
  routeErrorMessage,
} from './linkableRoutes';

/**
 * A linkable page that cannot be shown (invalid id, or the fetch for it
 * failed): toast why, then replace the page with the VP list.
 * `failure` is the failed response (`{ status, body }`), or nothing.
 */
export const leaveToVpList = (history, failure) => {
  addNotification({ type: 'danger', title: 'Not available', message: routeErrorMessage(failure) });
  history.replace(VALUE_PROPOSITIONS_LIST_PATH);
};

/**
 * Leaves an Add/Edit Fundamental form (after Save, Cancel or Delete) for the
 * VP's Fundamentals page without leaving the form in history: steps back when
 * the form was opened from that page (which refetches on mount), otherwise
 * replaces the form with it (e.g. opened from a pasted link). A form opened
 * from the VP list passes that on, so the Fundamentals page's back arrow
 * still steps back to the VP list.
 */
export const returnToFundamentals = (history, vpId) => {
  const state = history.location?.state;
  if (openedFromFundamentals(state, vpId)) {
    history.goBack();
  } else {
    history.replace(fundamentalsPath(vpId), openedFromVpList(state) ? fromVpListState() : undefined);
  }
};

/**
 * The back arrow of a page the VP list opens: steps back when the page was
 * opened from the VP list (so its filters and navigation state come back),
 * otherwise replaces the page with the VP list (never pushes, which would
 * make a Back/Forward loop).
 */
export const returnToVpList = (history) => {
  if (openedFromVpList(history.location?.state)) {
    history.goBack();
  } else {
    history.replace(VALUE_PROPOSITIONS_LIST_PATH);
  }
};

/**
 * Fetches a linkable page's data for its URL and says whether the store's
 * results may be used yet (see `linkablePageStatus`).
 *
 * `key` is the URL's ids as a string (`null` when invalid); `request()`
 * dispatches the fetches and runs on mount and whenever `key` changes.
 * `fetched`, `failure` and `rejected` are read from the store by the page.
 * Returns `{ ready, leave, failure }`.
 */
export const useUrlFetch = ({ key, request, fetched, failure, rejected }) => {
  // The URL whose fetches this mount has dispatched.
  const [requestedKey, setRequestedKey] = useState(null);

  useEffect(() => {
    if (key == null) return;
    request();
    setRequestedKey(key);
    // Fetch once per URL; `request` is a new closure on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return linkablePageStatus({ key, requestedKey, fetched, failure, rejected });
};

/**
 * `useUrlFetch` for a page that, when it cannot be shown (invalid id, failed
 * fetch, data of another VP), toasts why and is replaced by the VP list.
 * Returns `{ ready, leave, failure }`.
 */
export const useLinkablePage = ({ history, ...fetch }) => {
  const status = useUrlFetch(fetch);

  useEffect(() => {
    if (status.leave) leaveToVpList(history, status.failure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.leave]);

  return status;
};

/**
 * Route component for an old id-less path: forwards to the new URL built from
 * the navigation state, keeping that state (e.g. the starting tab).
 */
export const LegacyRedirect = ({ location }) => (
  <Redirect
    to={{
      pathname: resolveLegacyPath(location.pathname, location.state),
      state: location.state,
    }}
  />
);
